import type * as THREE from 'three';
import type { DashboardId, Ecg, Lab, Subject, Visit, Vitals } from '@clinspace/shared';
import { Dashboard, type RecordDetail, type ViewRegion, type WardAction } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import {
  PALETTE,
  chip,
  fillRoundRect,
  fitSize,
  font,
  hexToRgba,
  list,
  sparkline,
  text,
  truncate,
  type ListRow,
  type Rect,
} from '../charts.ts';
import {
  aeDetail,
  conMedDetail,
  deviationDetail,
  dosingDetail,
  formDetail,
  labDetail,
  queryDetail,
  subjectDetail,
  visitDetail,
  vitalsDetail,
} from '../details.ts';
import type { StudyView } from '../StudyView.ts';
import { STATUS_COLOUR } from '../../ward/layout.ts';

/**
 * Patient Profiles: subjects in study, flagged, average age, sex split; a card deck of
 * subject summaries; and the full profile of one subject with tabs (Demographics, Visits,
 * Adverse Events, Con meds, Labs, Vitals, Deviations, Queries, Notes), every value drillable
 * to its record. The same class draws the arc panel, the panel that unfolds beside a
 * mannequin in the ward, and a clone pinned to the table.
 */
export const PROFILE_TABS = [
  'Demographics',
  'Visits',
  'Adverse Events',
  'Con meds',
  'Labs',
  'Vitals',
  'Deviations',
  'Queries',
  'Notes',
] as const;
export type ProfileTab = (typeof PROFILE_TABS)[number];

const ROW_H = 20;

export class PatientProfilesDashboard extends Dashboard {
  readonly id: DashboardId = 'PatientProfile';
  private page = 0;

  /** The subject whose profile is open, from the first path segment. */
  currentSubject(): string | undefined {
    const [first] = this.path;
    return first?.startsWith('subject:') ? first.slice(8) : undefined;
  }

  currentTab(): ProfileTab {
    const seg = this.path[1];
    const name = seg?.startsWith('tab:') ? seg.slice(4) : undefined;
    return (PROFILE_TABS as readonly string[]).includes(name ?? '')
      ? (name as ProfileTab)
      : 'Demographics';
  }

  override setPath(path: string[]): void {
    this.page = 0;
    super.setPath(path);
  }

  override drillTo(segment: string): void {
    this.page = 0;
    super.drillTo(segment);
  }

  override back(): void {
    this.page = 0;
    super.back();
  }

  /** Opens a tab of the current subject (replacing any deeper path). */
  showTab(tab: ProfileTab): void {
    const subject = this.currentSubject();
    if (!subject) return;
    this.setPath(
      tab === 'Demographics' ? [`subject:${subject}`] : [`subject:${subject}`, `tab:${tab}`],
    );
  }

  protected override segmentLabel(segment: string): string {
    const i = segment.indexOf(':');
    const kind = segment.slice(0, i);
    const id = segment.slice(i + 1);
    switch (kind) {
      case 'subject':
      case 'tab':
      case 'analyte':
        return id;
      case 'visit':
        return this.view.visit(id)?.visitName ?? id;
      case 'form':
        return this.view.form(id)?.form ?? id;
      case 'ae':
        return this.view.ae(id)?.term ?? id;
      case 'conmed':
        return this.view.conMed(id)?.drug ?? id;
      case 'lab':
        return this.view.lab(id)?.visitCode ?? id;
      case 'vitals':
        return this.view.vitals(id)?.visitCode ?? id;
      case 'deviation':
        return this.view.deviation(id)?.category ?? id;
      case 'dosing':
        return this.view.dosing(id)?.visitCode ?? id;
      case 'ecg':
        return this.view.ecg(id)?.visitCode ?? id;
      case 'query':
        return `Query ${id.slice(-6)}`;
      default:
        return segment;
    }
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const subjectId = this.currentSubject();
    const subject = subjectId ? this.view.subject(subjectId) : undefined;
    if (subject) {
      this.drawProfile(ctx, panel, area, subject);
      return;
    }
    this.detail = undefined;
    this.drawOverview(ctx, panel, area);
  }

  /* ---------- Overview: KPIs and the card deck ---------- */

  private drawOverview(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const subjects = this.view.subjects(this.filters);
    const cards = this.view.file.dashboards.patientProfiles.cards.filter((c) =>
      subjects.some((s) => s.id === c.subjectId),
    );
    const flagged = cards.filter((c) => c.flags.length > 0).length;
    const avgAge = subjects.length
      ? Math.round((subjects.reduce((n, s) => n + s.age, 0) / subjects.length) * 10) / 10
      : 0;
    const f = subjects.filter((s) => s.sex === 'F').length;
    this.kpis(
      panel,
      ctx,
      [
        { label: 'Subjects', value: String(subjects.length) },
        {
          label: 'Flagged',
          value: String(flagged),
          colour: flagged ? PALETTE.amber : PALETTE.teal,
          sub: 'SAE, grade 3+ lab, deviation, missed visit, open query',
        },
        { label: 'Average age', value: String(avgAge) },
        { label: 'Sex split', value: `${f} F / ${subjects.length - f} M` },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const deck: Rect = { x: area.x, y: area.y + 88, w: area.w, h: area.h - 88 };
    const cols = 5;
    const rows = 3;
    const perPage = cols * rows;
    const pages = Math.max(1, Math.ceil(cards.length / perPage));
    this.page = Math.min(this.page, pages - 1);
    text(
      ctx,
      `Subject cards · page ${this.page + 1} of ${pages} · select a card for the full profile; the ward behind this panel has every subject`,
      deck.x,
      deck.y + 12,
      { size: 11, colour: PALETTE.muted, weight: 600, maxWidth: deck.w - 80 },
    );
    const cw = (deck.w - (cols - 1) * 8) / cols;
    const ch = (deck.h - 20 - (rows - 1) * 8) / rows;
    cards.slice(this.page * perPage, (this.page + 1) * perPage).forEach((c, i) => {
      const r = {
        x: deck.x + (i % cols) * (cw + 8),
        y: deck.y + 20 + Math.floor(i / cols) * (ch + 8),
        w: cw,
        h: ch,
      };
      const hovered = panel.isHovered(`card:${i}`);
      fillRoundRect(ctx, r, 8, hovered ? PALETTE.raisedHover : PALETTE.panel);
      ctx.fillStyle = c.saeLast30Days
        ? PALETTE.crimson
        : c.flags.length
          ? PALETTE.amber
          : PALETTE.teal;
      ctx.fillRect(r.x, r.y + 8, 4, r.h - 16);
      text(ctx, c.subjectId, r.x + 10, r.y + 16, { size: 12, weight: 700 });
      text(
        ctx,
        `${c.sex} ${c.age} · ${this.view.site(c.siteId)?.city ?? c.siteId}`,
        r.x + 10,
        r.y + 30,
        { size: 10, colour: PALETTE.muted },
      );
      ctx.font = font(10, 500);
      text(ctx, truncate(ctx, c.arm ?? 'not randomised', r.w - 16), r.x + 10, r.y + 43, {
        size: 10,
        colour: PALETTE.muted,
      });
      text(ctx, c.status, r.x + 10, r.y + 57, { size: 10, colour: PALETTE.text });
      text(
        ctx,
        `${c.visitsCompleted}/${c.visitsDue} visits · ${c.openQueries} open q`,
        r.x + 10,
        r.y + 70,
        { size: 9, colour: PALETTE.muted },
      );
      if (c.flags.length)
        text(ctx, truncate(ctx, c.flags.join(', '), r.w - 16), r.x + 10, r.y + 83, {
          size: 9,
          colour: PALETTE.amber,
        });
      panel.region<ViewRegion>({
        id: `card:${i}`,
        ...r,
        data: { kind: 'drill', segment: `subject:${c.subjectId}` },
        tooltip: [
          c.subjectId,
          `${c.status}${c.flags.length ? ` · ${c.flags.join(', ')}` : ''}`,
          'Open the full profile',
        ],
      });
    });
    this.pager(ctx, panel, deck.x + deck.w - 70, deck.y);
  }

  private pager(ctx: CanvasRenderingContext2D, panel: CanvasPanel, x: number, y: number): void {
    const prev = { x, y, w: 30, h: 16 };
    const next = { x: x + 36, y, w: 30, h: 16 };
    fillRoundRect(
      ctx,
      prev,
      8,
      panel.isHovered('page:prev') ? PALETTE.raisedHover : PALETTE.raised,
    );
    fillRoundRect(
      ctx,
      next,
      8,
      panel.isHovered('page:next') ? PALETTE.raisedHover : PALETTE.raised,
    );
    text(ctx, '‹', prev.x + 15, prev.y + 12, { size: 12, align: 'center' });
    text(ctx, '›', next.x + 15, next.y + 12, { size: 12, align: 'center' });
    panel.region<ViewRegion>({ id: 'page:prev', ...prev, data: { kind: 'info' } });
    panel.region<ViewRegion>({ id: 'page:next', ...next, data: { kind: 'info' } });
  }

  /* ---------- Profile: strip, tabs, tab content ---------- */

  private drawProfile(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    s: Subject,
  ): void {
    // Identity strip.
    text(ctx, s.id, area.x, area.y + 18, { size: 18, weight: 800 });
    ctx.font = font(18, 800);
    let x = area.x + ctx.measureText(s.id).width + 10;
    const status = chip(ctx, s.status, x, area.y + 2, {
      active: true,
      colour: STATUS_COLOUR[s.status],
      h: 20,
      size: 10,
    });
    x += status.w + 8;
    const flags = s.flags.length ? s.flags.join(' · ') : 'no flags';
    const stripRight = this.drawWardChips(ctx, panel, area, s.id);
    text(ctx, flags, x, area.y + 16, {
      size: 11,
      colour: s.flags.length ? PALETTE.amber : PALETTE.muted,
      maxWidth: stripRight - x - 8,
    });
    const site = this.view.site(s.siteId);
    const line2 = [
      `${s.sex === 'F' ? 'Female' : 'Male'} · ${s.age} y`,
      `Site ${s.siteId}${site ? ` ${site.city}` : ''}`,
      s.arm ?? 'not randomised',
      `consented ${s.consentDate}`,
      `${s.heightCm} cm · ${s.weightKg} kg · BMI ${s.bmi}`,
      `${this.view.openQueryCount(s.id)} open queries`,
    ].join('   ·   ');
    text(ctx, line2, area.x, area.y + 38, { size: 11, colour: PALETTE.muted, maxWidth: area.w });
    // Tabs.
    const tab = this.currentTab();
    let tx = area.x;
    for (const name of PROFILE_TABS) {
      const id = `tab:${name}`;
      const count = this.tabCount(name, s.id);
      const label = count !== undefined ? `${name} ${count}` : name;
      const r = chip(ctx, label, tx, area.y + 50, {
        active: name === tab,
        hovered: panel.isHovered(id),
        h: 22,
        size: 10,
      });
      panel.region<ViewRegion>({ id, ...r, data: { kind: 'info' }, tooltip: [`${name} tab`] });
      tx += r.w + 6;
    }
    const content: Rect = { x: area.x, y: area.y + 82, w: area.w, h: area.h - 82 };
    this.detail = undefined;
    const deep = this.path[2];
    if (deep) this.drawRecord(ctx, panel, content, s, deep);
    else this.drawTab(ctx, panel, content, s, tab);
  }

  /** Counts shown on the tab chips so a reviewer sees where the data is at a glance. */
  private tabCount(tab: ProfileTab, subjectId: string): number | undefined {
    switch (tab) {
      case 'Adverse Events':
        return this.view.file.adverseEvents.filter((a) => a.subjectId === subjectId).length;
      case 'Con meds':
        return this.view.file.conMeds.filter((c) => c.subjectId === subjectId).length;
      case 'Deviations':
        return this.view.file.deviations.filter((d) => d.subjectId === subjectId).length;
      case 'Queries':
        return this.view.queries.filter((q) => q.subjectId === subjectId).length;
      default:
        return undefined;
    }
  }

  private drawWardChips(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    subjectId: string,
  ): number {
    const ward = this.host.ward;
    if (!ward) return area.x + area.w;
    const inWard = ward.inWard(this);
    const items: { action: WardAction; label: string; active?: boolean; tip: string }[] = [];
    const pulled = ward.pulled(subjectId);
    items.push({
      action: 'pull',
      label: pulled ? 'Send back' : 'Pull out for review',
      active: pulled,
      tip: pulled
        ? 'Back to the formation'
        : 'The mannequin comes to you with its record and timeline; the dashboards stay as they are',
    });
    // The dashboards follow a patient only when asked (2026-10-06, the user's request).
    const filtered = ward.filtered(subjectId);
    items.push({
      action: 'filter',
      label: filtered ? 'Dashboards: this patient' : 'Filter dashboards',
      active: filtered,
      tip: filtered
        ? 'Every dashboard shows this patient; select to show everyone again'
        : 'Every dashboard, for everyone in the room, shows only this patient',
    });
    if (!inWard && !pulled)
      items.push({ action: 'go', label: 'Go to ward', tip: 'Stand in front of this mannequin' });
    if (!this.pinId) {
      const onTable = ward.onTable(subjectId);
      items.push({
        action: 'table',
        label: onTable ? 'Remove from table' : 'Bring to table',
        active: onTable,
        tip: onTable
          ? 'Takes the miniature and the pinned profile off the table'
          : 'A miniature and this profile on the table, for everyone',
      });
    }
    if (inWard)
      items.push({
        action: 'compare',
        label: this.compareTo ? 'Stop comparing' : 'Compare',
        active: !!this.compareTo,
        tip: 'Then select another mannequin',
      });
    items.push({
      action: 'timeline',
      label: ward.timelineOn(subjectId) ? 'Timeline on' : 'Walk the timeline',
      active: ward.timelineOn(subjectId),
      tip: 'Visit discs on the ward floor; step on one',
    });
    items.push({ action: 'overlays', label: ward.overlayLabel(), tip: 'Lab and AE body overlays' });
    let x = area.x + area.w;
    for (const item of [...items].reverse()) {
      ctx.font = font(10, 600);
      const w = ctx.measureText(item.label).width + 20;
      x -= w;
      const id = `ward:${item.action}`;
      const r = chip(ctx, item.label, x, area.y + 2, {
        active: item.active,
        hovered: panel.isHovered(id),
        h: 20,
        size: 10,
      });
      panel.region<ViewRegion>({ id, ...r, data: { kind: 'info' }, tooltip: [item.tip] });
      x -= 6;
    }
    return x;
  }

  private visitsOf(subjectId: string): Visit[] {
    return this.view.file.visits
      .filter((v) => v.subjectId === subjectId)
      .sort((a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode));
  }

  private labsOf(subjectId: string, analyte?: string): Lab[] {
    return this.view.file.labs
      .filter((l) => l.subjectId === subjectId && (!analyte || l.analyte === analyte))
      .sort((a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode));
  }

  private vitalsOf(subjectId: string): Vitals[] {
    return this.view.file.vitals
      .filter((v) => v.subjectId === subjectId)
      .sort((a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode));
  }

  private ecgsOf(subjectId: string): Ecg[] {
    return this.view.file.ecgs
      .filter((e) => e.subjectId === subjectId)
      .sort((a, b) => this.view.visitIndex(a.visitCode) - this.view.visitIndex(b.visitCode));
  }

  /** A paged list with a drill region per row. */
  private drillList(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    header: string[],
    rows: (ListRow & { segment: string; tooltip?: string[] })[],
    widths: number[],
    emptyText: string,
  ): void {
    const perPage = Math.max(1, Math.floor((area.h - 20) / ROW_H));
    const pages = Math.max(1, Math.ceil(rows.length / perPage));
    this.page = Math.min(this.page, pages - 1);
    const slice = rows.slice(this.page * perPage, (this.page + 1) * perPage);
    const hoveredIndex = slice.findIndex((_r, i) => panel.isHovered(`row:${i}`));
    const rects = list(ctx, header, slice, area, { widths, rowH: ROW_H, hoveredIndex, emptyText });
    rects.forEach((r, i) => {
      const row = slice[i]!;
      panel.region<ViewRegion>({
        id: `row:${i}`,
        ...r,
        data: { kind: 'drill', segment: row.segment },
        tooltip: row.tooltip ?? [row.cells[0] ?? '', 'Open the record'],
      });
    });
    if (pages > 1) {
      text(ctx, `${this.page + 1} / ${pages}`, area.x + area.w - 80, area.y + 12, {
        size: 10,
        colour: PALETTE.muted,
        align: 'right',
      });
      this.pager(ctx, panel, area.x + area.w - 70, area.y);
    }
  }

  private drawTab(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    s: Subject,
    tab: ProfileTab,
  ): void {
    const view = this.view;
    switch (tab) {
      case 'Demographics': {
        this.detail = subjectDetail(view, s);
        const other = this.compareSubject();
        if (other) this.drawCompare(ctx, panel, area, this.detail, subjectDetail(view, other));
        else this.drawDetail(ctx, panel, area, this.detail);
        return;
      }
      case 'Visits': {
        const forms = view.file.forms;
        const rows = this.visitsOf(s.id).map((v) => {
          const at = forms.filter((f) => f.visitId === v.id);
          const entered = at.filter((f) => f.status !== 'Not Started').length;
          const bad = v.status === 'Missed' || v.timing === 'Late' || v.timing === 'Early';
          return {
            cells: [
              v.visitName,
              v.targetDate,
              v.actualDate ?? (v.status === 'Missed' ? 'missed' : v.status.toLowerCase()),
              v.timing,
              at.length ? `${entered}/${at.length}` : '–',
              v.deviationDays !== undefined
                ? `${v.deviationDays > 0 ? '+' : ''}${v.deviationDays} d`
                : '–',
            ],
            flag: bad ? v.timing : undefined,
            colour: v.status === 'Missed' ? PALETTE.crimson : PALETTE.amber,
            segment: `visit:${v.id}`,
            tooltip: [v.visitName, `${v.status} · ${v.timing}`, 'Open the visit'],
          };
        });
        this.drillList(
          ctx,
          panel,
          area,
          ['Visit', 'Target', 'Actual', 'Timing', 'Forms', 'Deviation'],
          rows,
          [0.22, 0.17, 0.17, 0.14, 0.14, 0.16],
          'No visits yet',
        );
        return;
      }
      case 'Adverse Events': {
        const rows = view.file.adverseEvents
          .filter((a) => a.subjectId === s.id)
          .sort((a, b) => a.onsetDate.localeCompare(b.onsetDate))
          .map((a) => ({
            cells: [
              a.term,
              a.onsetDate,
              a.ongoing ? 'ongoing' : (a.resolutionDate ?? '–'),
              String(a.grade),
              a.serious ? 'SAE' : 'no',
              a.relatedness,
              a.outcome,
            ],
            flag: a.serious ? 'SAE' : a.grade >= 3 ? 'grade 3+' : undefined,
            colour: a.serious ? PALETTE.crimson : PALETTE.amber,
            segment: `ae:${a.id}`,
            tooltip: [a.term, `Grade ${a.grade}${a.serious ? ' · serious' : ''}`, 'Open the AE'],
          }));
        this.drillList(
          ctx,
          panel,
          area,
          ['Term', 'Onset', 'Resolved', 'Grade', 'Serious', 'Related', 'Outcome'],
          rows,
          [0.26, 0.13, 0.13, 0.08, 0.1, 0.14, 0.16],
          'No adverse events recorded',
        );
        return;
      }
      case 'Con meds': {
        const rows = view.file.conMeds
          .filter((c) => c.subjectId === s.id)
          .sort((a, b) => a.startDate.localeCompare(b.startDate))
          .map((c) => ({
            cells: [
              c.drug,
              c.indication,
              `${c.dose} ${c.unit} ${c.frequency}`,
              c.startDate,
              c.ongoing ? 'ongoing' : (c.endDate ?? '–'),
            ],
            segment: `conmed:${c.id}`,
            tooltip: [c.drug, c.indication, 'Open the medication'],
          }));
        this.drillList(
          ctx,
          panel,
          area,
          ['Medication', 'Indication', 'Dose', 'Start', 'End'],
          rows,
          [0.24, 0.26, 0.2, 0.15, 0.15],
          'No concomitant medications recorded',
        );
        return;
      }
      case 'Labs':
        this.drawLabGrid(ctx, panel, area, s);
        return;
      case 'Vitals': {
        const vitals = this.vitalsOf(s.id);
        const rows = vitals.map((v, i) => {
          const prev = vitals[i - 1];
          const delta = prev ? v.weightKg - prev.weightKg : 0;
          return {
            cells: [
              v.visitCode,
              v.date,
              `${v.systolicBp}/${v.diastolicBp}`,
              String(v.heartRate),
              `${v.temperatureC}`,
              String(v.respiratoryRate),
              `${v.weightKg} kg${prev ? ` (${delta > 0 ? '+' : ''}${delta.toFixed(1)})` : ''}`,
            ],
            flag: Math.abs(delta) >= 10 ? 'weight' : undefined,
            colour: PALETTE.crimson,
            segment: `vitals:${v.id}`,
            tooltip: [`Vitals · ${v.visitCode}`, `${v.weightKg} kg`, 'Open the record'],
          };
        });
        const ecgs = this.ecgsOf(s.id);
        const ecgRows = ecgs.map((e) => ({
          cells: [
            `ECG ${e.visitCode}`,
            e.date,
            `QTcF ${e.qtcfMs} ms`,
            String(e.heartRate),
            `PR ${e.prMs}`,
            `QRS ${e.qrsMs}`,
            e.interpretation,
          ],
          flag: e.interpretation === 'Abnormal, clinically significant' ? 'ECG' : undefined,
          colour: PALETTE.crimson,
          segment: `ecg:${e.id}`,
          tooltip: [`ECG · ${e.visitCode}`, e.interpretation, 'Open the ECG'],
        }));
        this.drillList(
          ctx,
          panel,
          area,
          ['Visit', 'Date', 'BP', 'HR', 'Temp °C', 'RR', 'Weight'],
          [...rows, ...ecgRows],
          [0.12, 0.14, 0.12, 0.08, 0.1, 0.1, 0.34],
          'No vitals recorded',
        );
        return;
      }
      case 'Deviations': {
        const rows = view.file.deviations
          .filter((d) => d.subjectId === s.id)
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((d) => ({
            cells: [
              d.category,
              d.classification ?? 'Unclassified',
              d.date,
              d.visitCode ?? '–',
              d.status,
              d.description,
            ],
            flag:
              d.classification === null
                ? 'unclassified'
                : d.classification === 'Major'
                  ? 'major'
                  : undefined,
            colour: d.classification === null ? PALETTE.amber : PALETTE.crimson,
            segment: `deviation:${d.id}`,
            tooltip: [d.category, d.classification ?? 'Unclassified', 'Open the deviation'],
          }));
        this.drillList(
          ctx,
          panel,
          area,
          ['Category', 'Class', 'Date', 'Visit', 'Status', 'Description'],
          rows,
          [0.16, 0.12, 0.13, 0.08, 0.1, 0.41],
          'No protocol deviations recorded',
        );
        return;
      }
      case 'Queries': {
        const rows = view.queries
          .filter((q) => q.subjectId === s.id)
          .sort((a, b) => b.raisedAt.localeCompare(a.raisedAt))
          .map((q) => ({
            cells: [q.raisedAt.slice(0, 10), q.form, q.field ?? '–', q.status, q.priority, q.text],
            flag: q.status === 'Open' ? 'open' : undefined,
            colour: q.priority === 'High' ? PALETTE.crimson : PALETTE.amber,
            segment: `query:${q.id}`,
            tooltip: [`${q.status} · ${q.priority}`, q.text.slice(0, 60), 'Open the query'],
          }));
        this.drillList(
          ctx,
          panel,
          area,
          ['Raised', 'Form', 'Field', 'Status', 'Priority', 'Text'],
          rows,
          [0.12, 0.17, 0.16, 0.1, 0.09, 0.36],
          'No queries on this subject',
        );
        return;
      }
      case 'Notes':
        this.drawNotes(ctx, panel, area, s);
        return;
    }
  }

  /* ---------- Labs: sparkline grid and the per-analyte series ---------- */

  private drawLabGrid(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    s: Subject,
  ): void {
    const labs = this.labsOf(s.id);
    const analytes = [...new Set(labs.map((l) => l.analyte))];
    if (!analytes.length) {
      text(ctx, 'No laboratory results yet', area.x + 4, area.y + 24, {
        size: 11,
        colour: PALETTE.muted,
      });
      return;
    }
    const cols = 5;
    const rows = Math.ceil(analytes.length / cols);
    const gap = 8;
    const cw = (area.w - gap * (cols - 1)) / cols;
    const ch = Math.min(110, (area.h - gap * (rows - 1)) / rows);
    analytes.forEach((analyte, i) => {
      const series = labs.filter((l) => l.analyte === analyte);
      const last = series[series.length - 1]!;
      const maxGrade = Math.max(...series.map((l) => l.grade));
      const r = {
        x: area.x + (i % cols) * (cw + gap),
        y: area.y + Math.floor(i / cols) * (ch + gap),
        w: cw,
        h: ch,
      };
      const id = `analyte:${analyte}`;
      const tint =
        maxGrade >= 3
          ? hexToRgba(PALETTE.crimson, 0.22)
          : maxGrade === 2
            ? hexToRgba(PALETTE.amber, 0.2)
            : maxGrade === 1
              ? hexToRgba(PALETTE.amber, 0.1)
              : PALETTE.panel;
      fillRoundRect(ctx, r, 8, panel.isHovered(id) ? PALETTE.raisedHover : tint);
      text(ctx, analyte, r.x + 8, r.y + 15, { size: 11, weight: 700, maxWidth: r.w - 60 });
      text(ctx, `G${maxGrade}`, r.x + r.w - 8, r.y + 15, {
        size: 10,
        weight: 700,
        align: 'right',
        colour: maxGrade >= 3 ? PALETTE.crimson : maxGrade ? PALETTE.amber : PALETTE.muted,
      });
      text(ctx, `${last.value} ${last.unit}`, r.x + 8, r.y + 30, {
        size: 10,
        colour: last.grade ? PALETTE.amber : PALETTE.muted,
        maxWidth: r.w - 16,
      });
      sparkline(
        ctx,
        series.map((l) => l.value),
        { x: r.x + 8, y: r.y + 38, w: r.w - 16, h: r.h - 48 },
        PALETTE.teal,
        { low: last.low, high: last.high },
      );
      panel.region<ViewRegion>({
        id,
        ...r,
        data: { kind: 'drill', segment: id },
        tooltip: [
          analyte,
          `${series.length} results · worst grade ${maxGrade} · last ${last.value} ${last.unit}`,
          'Open the series',
        ],
      });
    });
  }

  private drawLabSeries(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    s: Subject,
    analyte: string,
  ): void {
    const series = this.labsOf(s.id, analyte);
    const last = series[series.length - 1];
    if (!last) return;
    text(
      ctx,
      `${analyte} over visits · reference ${last.low} – ${last.high} ${last.unit}`,
      area.x,
      area.y + 12,
      {
        size: 11,
        weight: 600,
        colour: PALETTE.muted,
      },
    );
    sparkline(
      ctx,
      series.map((l) => l.value),
      { x: area.x + 4, y: area.y + 22, w: area.w - 8, h: 70 },
      PALETTE.teal,
      { low: last.low, high: last.high },
    );
    const rows = series.map((l) => ({
      cells: [
        l.visitCode,
        l.date,
        `${l.value} ${l.unit}`,
        l.abnormalFlag ?? '–',
        `Grade ${l.grade}`,
        l.ulnMultiple !== undefined ? `${l.ulnMultiple}× ULN` : '–',
        l.clinicallySignificant ?? '–',
      ],
      flag: l.grade >= 2 ? `grade ${l.grade}` : undefined,
      colour: l.grade >= 3 ? PALETTE.crimson : PALETTE.amber,
      segment: `lab:${l.id}`,
      tooltip: [
        `${analyte} · ${l.visitCode}`,
        `${l.value} ${l.unit} · grade ${l.grade}`,
        'Open the result',
      ],
    }));
    this.drillList(
      ctx,
      panel,
      { x: area.x, y: area.y + 100, w: area.w, h: area.h - 100 },
      ['Visit', 'Date', 'Value', 'Flag', 'Grade', 'ULN', 'Clin. sig.'],
      rows,
      [0.12, 0.16, 0.18, 0.1, 0.14, 0.14, 0.16],
      'No results',
    );
  }

  /* ---------- Records under a tab ---------- */

  private drawRecord(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    s: Subject,
    segment: string,
  ): void {
    const view = this.view;
    const i = segment.indexOf(':');
    const kind = segment.slice(0, i);
    const id = segment.slice(i + 1);
    const deeper = this.path[3];
    if (kind === 'analyte') {
      if (deeper?.startsWith('lab:')) {
        const l = view.lab(deeper.slice(4));
        if (l) this.showDetail(ctx, panel, area, labDetail(view, l));
      } else this.drawLabSeries(ctx, panel, area, s, id);
      return;
    }
    if (kind === 'visit') {
      const v = view.visit(id);
      if (!v) return;
      if (deeper?.startsWith('form:')) {
        const f = view.form(deeper.slice(5));
        if (f) this.showDetail(ctx, panel, area, formDetail(view, f));
        return;
      }
      this.showDetail(ctx, panel, area, visitDetail(view, v), 270);
      this.drawVisitForms(
        ctx,
        panel,
        { x: area.x, y: area.y + 272, w: area.w, h: area.h - 272 },
        v,
      );
      return;
    }
    const detail = this.recordDetail(kind, id, s);
    if (detail) this.showDetail(ctx, panel, area, detail);
  }

  private recordDetail(kind: string, id: string, s: Subject): RecordDetail | undefined {
    const view = this.view;
    switch (kind) {
      case 'ae': {
        const a = view.ae(id);
        return a && aeDetail(view, a);
      }
      case 'conmed': {
        const c = view.conMed(id);
        return c && conMedDetail(view, c);
      }
      case 'vitals': {
        const v = view.vitals(id);
        if (!v) return undefined;
        const all = this.vitalsOf(s.id);
        const idx = all.findIndex((x) => x.id === v.id);
        return vitalsDetail(view, v, idx > 0 ? all[idx - 1] : undefined);
      }
      case 'ecg': {
        const e = view.ecg(id);
        return e && ecgDetail(view, e);
      }
      case 'deviation': {
        const d = view.deviation(id);
        return d && deviationDetail(view, d);
      }
      case 'dosing': {
        const d = view.dosing(id);
        return d && dosingDetail(view, d);
      }
      case 'query': {
        const q = view.queries.find((x) => x.id === id);
        return q && queryDetail(view, q);
      }
      case 'form': {
        const f = view.form(id);
        return f && formDetail(view, f);
      }
      case 'lab': {
        const l = view.lab(id);
        return l && labDetail(view, l);
      }
      default:
        return undefined;
    }
  }

  private showDetail(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    detail: RecordDetail,
    height = area.h,
  ): void {
    this.detail = detail;
    this.drawDetail(ctx, panel, { ...area, h: height }, detail);
  }

  /** The CRF pages at a visit as chips, each opening the page. */
  private drawVisitForms(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    v: Visit,
  ): void {
    const forms = this.view.file.forms.filter((f) => f.visitId === v.id);
    text(
      ctx,
      forms.length ? 'Pages at this visit' : 'No pages expected at this visit',
      area.x,
      area.y + 12,
      {
        size: 11,
        weight: 600,
        colour: PALETTE.muted,
      },
    );
    let x = area.x;
    let y = area.y + 20;
    forms.forEach((f, i) => {
      const missing = f.missingFields.length;
      const label = `${f.form}: ${f.status}${missing ? ` · ${missing} missing` : ''}`;
      ctx.font = font(10, 600);
      const w = ctx.measureText(label).width + 20;
      if (x + w > area.x + area.w) {
        x = area.x;
        y += 26;
      }
      if (y + 22 > area.y + area.h) return;
      const id = `form:${i}`;
      const r = chip(ctx, label, x, y, {
        hovered: panel.isHovered(id),
        h: 22,
        size: 10,
        active: f.status === 'Not Started' && f.overdue,
        colour: PALETTE.amber,
      });
      panel.region<ViewRegion>({
        id,
        ...r,
        data: { kind: 'drill', segment: `form:${f.id}` },
        tooltip: [f.form, `${f.status}${f.overdue ? ' · overdue' : ''}`, 'Open the page'],
      });
      x += r.w + 6;
    });
  }

  /* ---------- Notes: what a reviewer should look at first ---------- */

  private drawNotes(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    s: Subject,
  ): void {
    const view = this.view;
    const notes: {
      cells: string[];
      segment: string;
      flag?: string;
      colour?: string;
      tooltip?: string[];
    }[] = [];
    const aes = view.file.adverseEvents.filter((a) => a.subjectId === s.id);
    const saes = aes.filter((a) => a.serious);
    for (const a of saes)
      notes.push({
        cells: [
          'Serious adverse event',
          `${a.term}, onset ${a.onsetDate}, grade ${a.grade}, ${a.outcome.toLowerCase()}`,
        ],
        segment: `ae:${a.id}`,
        flag: 'SAE',
        colour: PALETTE.crimson,
        tooltip: [a.term, 'Open the SAE'],
      });
    for (const a of aes)
      if (s.firstDoseDate && a.onsetDate < s.firstDoseDate)
        notes.push({
          cells: [
            'AE before first dose',
            `${a.term} onset ${a.onsetDate}, first dose ${s.firstDoseDate}`,
          ],
          segment: `ae:${a.id}`,
          flag: 'check',
          colour: PALETTE.amber,
        });
    const labs = this.labsOf(s.id).filter((l) => l.grade >= 3);
    for (const l of labs)
      notes.push({
        cells: [
          'Grade 3+ laboratory value',
          `${l.analyte} ${l.value} ${l.unit} at ${l.visitCode} (${l.date})`,
        ],
        segment: `lab:${l.id}`,
        flag: `G${l.grade}`,
        colour: PALETTE.crimson,
      });
    if (s.flags.includes("Hy's law candidate"))
      notes.push({
        cells: [
          "Hy's law candidate",
          'ALT above 3x ULN with bilirubin above 2x ULN at the same visit',
        ],
        segment: 'analyte:ALT',
        flag: 'liver',
        colour: PALETTE.crimson,
      });
    for (const v of this.visitsOf(s.id)) {
      if (v.status === 'Missed')
        notes.push({
          cells: ['Missed visit', `${v.visitName}: ${v.missedReason ?? 'no reason recorded'}`],
          segment: `visit:${v.id}`,
          flag: 'missed',
          colour: PALETTE.amber,
        });
      else if (v.timing === 'Late' || v.timing === 'Early')
        notes.push({
          cells: [
            `${v.timing} visit`,
            `${v.visitName}: ${v.actualDate} is ${Math.abs(v.deviationDays ?? 0)} days ${v.timing === 'Late' ? 'after' : 'before'} target`,
          ],
          segment: `visit:${v.id}`,
        });
    }
    for (const d of view.file.deviations.filter((d) => d.subjectId === s.id))
      notes.push({
        cells: [
          `${d.classification ?? 'Unclassified'} deviation`,
          `${d.category}: ${d.description}`,
        ],
        segment: `deviation:${d.id}`,
        flag:
          d.classification === 'Major'
            ? 'major'
            : d.classification === null
              ? 'classify'
              : undefined,
        colour: d.classification === 'Major' ? PALETTE.crimson : PALETTE.amber,
      });
    for (const d of view.file.dosing.filter(
      (d) => d.subjectId === s.id && (d.missedDoses > 0 || !d.administered),
    ))
      notes.push({
        cells: [
          'Dosing',
          `${d.visitCode}: ${d.administered ? `${d.missedDoses} doses missed` : 'not administered'}${d.reason ? ` (${d.reason})` : ''}`,
        ],
        segment: `dosing:${d.id}`,
        flag: d.missedDoses > 2 || !d.administered ? 'dosing' : undefined,
        colour: PALETTE.amber,
      });
    const vitals = this.vitalsOf(s.id);
    vitals.forEach((v, i) => {
      const prev = vitals[i - 1];
      if (prev && Math.abs(v.weightKg - prev.weightKg) >= 10)
        notes.push({
          cells: [
            'Weight change',
            `${prev.weightKg} kg at ${prev.visitCode} to ${v.weightKg} kg at ${v.visitCode}`,
          ],
          segment: `vitals:${v.id}`,
          flag: 'check',
          colour: PALETTE.amber,
        });
    });
    for (const e of this.ecgsOf(s.id))
      if (e.qtcfMs > 450)
        notes.push({
          cells: ['QTcF prolonged', `${e.qtcfMs} ms at ${e.visitCode} (${e.interpretation})`],
          segment: `ecg:${e.id}`,
          flag: e.qtcfMs > 480 ? 'QTc' : undefined,
          colour: PALETTE.crimson,
        });
    const open = view.queries.filter((q) => q.subjectId === s.id && q.status === 'Open');
    for (const q of open)
      notes.push({
        cells: ['Open query', `${q.form}${q.field ? ` · ${q.field}` : ''}: ${q.text}`],
        segment: `query:${q.id}`,
        flag: q.priority === 'High' ? 'high' : undefined,
        colour: PALETTE.amber,
      });
    if (s.discontinuationReason)
      notes.push({
        cells: ['Discontinued', `${s.discontinuationReason} on ${s.discontinuationDate}`],
        segment: 'tab:Demographics',
      });
    if (s.screenFailReason)
      notes.push({ cells: ['Screen failure', s.screenFailReason], segment: 'tab:Demographics' });
    if (!notes.length)
      notes.push({
        cells: ['Nothing flagged', 'Visits on time, no SAEs, no grade 3+ labs, no open queries'],
        segment: 'tab:Visits',
      });
    this.drillList(ctx, panel, area, ['Note', 'Detail'], notes, [0.24, 0.76], 'Nothing to note');
  }

  /* ---------- Compare ---------- */

  private compareSubject(): Subject | undefined {
    const other = this.compareTo;
    if (!other || !(other instanceof PatientProfilesDashboard)) return undefined;
    const id = other.currentSubject();
    if (!id || id === this.currentSubject()) return undefined;
    return this.view.subject(id);
  }

  /** Two-column demographics with the other subject's value beside each, differences in amber. */
  private drawCompare(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    area: Rect,
    mine: RecordDetail,
    theirs: RecordDetail,
  ): void {
    text(ctx, mine.title, area.x, area.y + 18, { size: 15, weight: 700 });
    text(ctx, `vs ${theirs.title}`, area.x + area.w, area.y + 18, {
      size: 12,
      colour: PALETTE.teal,
      align: 'right',
    });
    const rowH = 20;
    const labelW = Math.min(150, area.w * 0.22);
    const valueW = (area.w - labelW) / 2;
    let differing = 0;
    mine.rows.forEach((row, i) => {
      const y = area.y + 30 + i * rowH;
      if (y + rowH > area.y + area.h) return;
      const other = theirs.rows.find((r) => r.label === row.label)?.value ?? '—';
      const differs = other !== row.value;
      if (differs) differing++;
      const r = { x: area.x, y, w: area.w, h: rowH };
      if (panel.isHovered(`record:${i}`)) fillRoundRect(ctx, r, 4, PALETTE.hover);
      else if (differs) fillRoundRect(ctx, r, 4, hexToRgba(PALETTE.amber, 0.12));
      text(ctx, row.label, r.x + 6, y + 14, { size: 11, colour: PALETTE.muted });
      ctx.font = font(11, 500);
      text(ctx, truncate(ctx, row.value, valueW - 12), r.x + labelW, y + 14, {
        size: 11,
        colour: differs ? PALETTE.amber : PALETTE.text,
      });
      text(ctx, truncate(ctx, other, valueW - 12), r.x + labelW + valueW, y + 14, {
        size: 11,
        colour: differs ? PALETTE.amber : PALETTE.muted,
      });
      if (row.field)
        panel.region<ViewRegion>({
          id: `record:${i}`,
          ...r,
          data: { kind: 'record', field: row.field },
          tooltip: this.host.canRaiseQueries ? [`Raise a query on "${row.field}"`] : ['View only'],
        });
    });
    text(ctx, `${differing} fields differ`, area.x + area.w, area.y + area.h - 6, {
      size: fitSize(ctx, `${differing} fields differ`, 160, 10, 8),
      colour: PALETTE.muted,
      align: 'right',
    });
  }

  /* ---------- Info regions: tabs, paging, ward actions ---------- */

  protected override onInfo(regionId: string, point?: THREE.Vector3): void {
    if (regionId === 'page:prev') this.nextPage(-1);
    else if (regionId === 'page:next') this.nextPage(1);
    else if (regionId.startsWith('tab:')) this.showTab(regionId.slice(4) as ProfileTab);
    else if (regionId.startsWith('ward:')) {
      const subject = this.currentSubject();
      if (subject && this.host.ward && point)
        this.host.ward.action(this, regionId.slice(5) as WardAction, subject, point);
    }
  }

  nextPage(delta: number): void {
    this.page = Math.max(0, this.page + delta);
    this.invalidate();
  }
}

function ecgDetail(view: StudyView, e: Ecg): RecordDetail {
  const long = e.qtcfMs > 450;
  return {
    ref: view.ref(e.subjectId, 'ECG', e.id, view.visitName(e.visitId)),
    title: `ECG · ${e.subjectId} · ${e.visitCode}`,
    currentValue: `QTcF ${e.qtcfMs} ms`,
    rows: [
      { label: 'Date', value: e.date, field: 'ECG date' },
      { label: 'Heart rate', value: `${e.heartRate} bpm`, field: 'Heart rate' },
      { label: 'PR', value: `${e.prMs} ms`, field: 'PR interval' },
      { label: 'QRS', value: `${e.qrsMs} ms`, field: 'QRS duration' },
      { label: 'QT', value: `${e.qtMs} ms`, field: 'QT interval' },
      { label: 'QTcF', value: `${e.qtcfMs} ms`, field: 'QTcF', flag: long },
      {
        label: 'Interpretation',
        value: e.interpretation,
        field: 'Interpretation',
        flag: e.interpretation === 'Abnormal, clinically significant',
      },
    ],
  };
}
